/**
 * Admin-Karte: Pflicht-Benachrichtigungen (Joerg-Regel 2026-07-05).
 * Admin waehlt welche Slugs fuer normale Anwender NICHT abschaltbar sein
 * sollen. GF + Admin duerfen ohnehin alles fuer sich konfigurieren
 * (ArbZG § 2). Nur fuer Portal-Admins sichtbar (Route selbst prueft das,
 * hier optische Sperre + Fehler-Handling).
 *
 * Seit 2026-09-30 (Regel „Dokument-Navigation" E10, Abhakliste 1.46): Abschnitt zeigt
 * zuerst an, „Bearbeiten" öffnet die Haken, „Speichern" und „Abbrechen" führen zurück
 * in die Anzeige.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Lock, RefreshCw } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';
import { SettingsSection } from '@onvice/portal-ui';

type Channel = 'in_app' | 'push_web' | 'push_mobile' | 'email';

interface MandatoryResponse {
  success: boolean;
  mandatory_slugs: string[];
  slug_labels: Record<string, string>;
  slug_defaults: Record<string, Channel[]>;
}

interface Mandatory { slugs: string[] }

export default function NotificationMandatorySlugsCard() {
  // 2026-09-06 (A4-Beifang, HW-AD-EINSTELLUNGEN-001): isAdmin VOR dem Fetch
  // pruefen statt den 403 abzuwarten — der Admin-Call feuerte fuer jeden
  // Anwender auf /settings und verrauschte den Smoke.
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mandatory, setMandatory] = useState<Mandatory | null>(null);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get<MandatoryResponse>('/notifications/admin/mandatory-slugs');
      setMandatory({ slugs: [...(r.mandatory_slugs ?? [])].sort() });
      setLabels(r.slug_labels ?? {});
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      if (status === 403 || status === 401) {
        // Nicht-Admin: Card ausblenden statt Fehlermeldung.
        setVisible(false);
      } else {
        setLoadError((err as Error).message || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) { setVisible(false); setLoading(false); return; }
    void load();
  }, [isAdmin, load]);

  const save = useCallback(async (value: Mandatory): Promise<Mandatory> => {
    await api.put('/notifications/admin/mandatory-slugs', { slugs: value.slugs });
    return { slugs: [...value.slugs].sort() };
  }, []);

  if (!visible) return null;

  const sortedSlugs = Object.keys(labels).sort();

  return (
    <div className="bg-card border border-default rounded-xl p-4 sm:p-5 mb-4"
         data-test-anchor="pflicht-benachrichtigungen">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={18} className="text-primary" />
        <h2 className="text-lg font-semibold">Pflicht-Benachrichtigungen (Admin)</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Diese Ereignisse werden allen Anwendern per Web-Push zugestellt und
        können von ihnen NICHT abgeschaltet werden. Typisch für gesetzliche
        Compliance-Meldungen (ArbZG, BUrlG). Geschäftsführung und Admin
        dürfen ihre eigenen Kanäle weiterhin frei konfigurieren.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-3">
          <Loader2 size={14} className="animate-spin" /> Lade…
        </div>
      ) : loadError ? (
        <div className="rounded-md bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-rose-400 flex flex-wrap items-center gap-3" data-test-anchor="pflicht-benachrichtigungen-ladefehler">
          <span>Nicht geladen: {loadError}</span>
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-xs hover:bg-rose-500/10">
            <RefreshCw size={12} /> Erneut laden
          </button>
        </div>
      ) : (
        <SettingsSection<Mandatory>
          id="benachrichtigungen:pflicht"
          title="Nicht abschaltbare Ereignisse"
          baseline={mandatory}
          testAnchor="pflicht-slugs"
          onSave={save}
          renderRead={(v) => (
            v.slugs.length === 0
              ? <div className="text-sm text-muted-foreground">Keine Pflicht-Benachrichtigungen festgelegt.</div>
              : (
                <ul className="space-y-1 text-sm">
                  {v.slugs.map((slug) => (
                    <li key={slug} className="flex items-center gap-2">
                      <Lock size={12} className="text-muted-foreground" />
                      <span>{labels[slug] ?? slug}</span>
                      <span className="text-[11px] text-muted-foreground">{slug}</span>
                    </li>
                  ))}
                </ul>
              )
          )}
          renderEdit={(v, setField) => {
            const selected = new Set(v.slugs);
            const toggle = (slug: string) => {
              const next = new Set(selected);
              if (next.has(slug)) next.delete(slug); else next.add(slug);
              setField('slugs', [...next].sort());
            };
            return (
              <ul className="space-y-1 max-h-96 overflow-y-auto">
                {sortedSlugs.map((slug) => (
                  <li key={slug}>
                    <label className="flex items-start gap-2 p-2 rounded-md hover:bg-background/50 cursor-pointer">
                      <input type="checkbox" checked={selected.has(slug)} onChange={() => toggle(slug)} className="mt-1 w-4 h-4" />
                      <div className="flex-1">
                        <div className="text-sm">{labels[slug] ?? slug}</div>
                        <div className="text-[11px] text-muted-foreground">{slug}</div>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            );
          }}
        />
      )}
    </div>
  );
}
